import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { Button } from "@mui/material";
import {
  employeeFieldConfiguration,
  makeFieldConfigDynamic,
} from "../../../formConfigs/HR/employeeFieldConfig";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import StatusSnackbar from "../../General/StatusSnackbar/StatusSnackbar";
import { OptionType } from "../../../interfaces/Fleet/IGenericFormPopupProps";
import GenericFormPopup from "../../Fleet/GenericFormPopup/GenericFormPopup";
import { capitalizeFirstChar } from "../../../helpers/formatingFunctions";
import { PayFrequencyEnum } from "../../../data/HR/PayFrequency";
import { validateAndFormatChildrenDetails } from "../../../helpers/employeeChildrenValidation";
import { normalizeEmployeeGender } from "../../../data/HR/EmployeeGender";

export const fetchManagers = async (token: string) => {
  const response = await axios.get(`${apiVariable}/getAllManagers`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data.map((manager: any) => ({
    label: `${manager.firstName} ${manager.lastName} | ${manager.position}`,
    id: manager.id.toString(),
  }));
};

const UpdateEmployeePopup = ({
  employee,
  onUpdated,
}: {
  employee: any;
  onUpdated?: (employee: any) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [reportsToData, setReportsToData] = useState<OptionType[]>([]);
  const [secondLevelManagerData, setSecondLevelManagerData] = useState<
    OptionType[]
  >([]);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [initialValues, setInitialValues] = useState<any>({});

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleSnackbarClose = () => setStatus("idle");

  const queryClient = useQueryClient();
  const { token } = useAuth();

  const {
    data: managersData,
    isError,
    error,
  } = useQuery({
    queryKey: ["managers"],
    queryFn: () => fetchManagers(token!),
    enabled: !!token,
  });

  useEffect(() => {
    if (managersData) {
      setReportsToData(managersData);
      setSecondLevelManagerData(managersData);
    }
  }, [managersData]);

  useEffect(() => {
    if (isError && error) {
      setStatus("error");
      setErrorMessage(
        (error as any).response?.data || "Failed to fetch managers"
      );
    }
  }, [isError, error]);

  const employeeMutation = useMutation({
    mutationFn: (data: any) =>
      axios.put(`${apiVariable}/updateEmployee`, data, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    onSuccess: (response) => {
      setStatus("success");
      handleClose();
      queryClient.invalidateQueries({ queryKey: ["employees"] });
      onUpdated?.(response.data);
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(error.response?.data || "Failed to update employee");
    },
  });

  const submitHandler = (formData: any) => {
    const data = {
      ...formData,
      contractType: capitalizeFirstChar(employee.contractType),
    };
    const requiredFields = [
      "firstName",
      "lastName",
      "position",
      "department",
      "contractType",
      "payFrequency",
      "startDate",
      "email",
      "phoneNumber",
      "isMarried",
      "childrenCount",
      "employeeId",
    ];

    const isMissingValue = (value: any) =>
      value === undefined || value === null || value === "";

    const missingFields = requiredFields.filter((field) =>
      isMissingValue(data[field])
    );

    if (missingFields.length > 0) {
      setStatus("error");
      setErrorMessage("Please fill in all required fields.");
      return;
    }

    const {
      isValid,
      message: childrenMessage,
      count,
      formattedSexes,
    } = validateAndFormatChildrenDetails(
      data.childrenCount,
      data.childrenSexes
    );

    if (!isValid) {
      setStatus("error");
      setErrorMessage(childrenMessage || "Invalid children details.");
      return;
    }

    const submitData = {
      ...data,
      gender: normalizeEmployeeGender(data.gender),
      reportsToId: getId(data.reportsToId),
      secondLevelManager: getId(data.secondLevelManager),
      id: employee.id,
      terminationDate:
        data.terminationDate === "" ? null : data.terminationDate,
      childrenCount: count,
      childrenSexes: formattedSexes || null,
    };

    delete submitData.manager;
    delete submitData.secondManager;

    employeeMutation.mutate(submitData);
  };

  const dynamicFields = makeFieldConfigDynamic(employeeFieldConfiguration).map(
    (field) =>
      field.name === "contractType" ? { ...field, disabled: true } : field
  );

  useEffect(() => {
    setInitialValues({
      firstName: employee.firstName,
      lastName: employee.lastName,
      nickname: employee.nickname ?? "",
      position: employee.position,
      legalPosition: employee.legalPosition ?? "",
      gender: normalizeEmployeeGender(employee.gender) ?? "",
      department: employee.department,
      reportsToId: employee.manager
        ? {
          label: `${employee.manager.firstName} ${employee.manager.lastName} | ${employee.manager.position}`,
          id: employee.manager.id.toString(),
        }
        : null,
      contractType: capitalizeFirstChar(employee.contractType),
      payFrequency: employee.payFrequency
        ? capitalizeFirstChar(employee.payFrequency)
        : PayFrequencyEnum.Monthly,
      employer: employee.employer ?? "",
      startDate: employee.startDate.split("T")[0],
      terminationDate: employee.terminationDate
        ? employee.terminationDate.split("T")[0]
        : "",
      email: employee.email,
      phoneNumber: employee.phoneNumber,
      workPhoneNumber: employee.workPhoneNumber ?? "",
      isMarried: employee.isMarried,
      childrenCount: employee.childrenCount ?? 0,
      childrenSexes: employee.childrenSexes ?? "",
      birthDate: employee.birthDate.split("T")[0],
      secondLevelManager: employee.secondManager
        ? {
          label: `${employee.secondManager.firstName} ${employee.secondManager.lastName} | ${employee.secondManager.position}`,
          id: employee.secondManager.id.toString(),
        }
        : null,
      isManager: employee.isManager,
      deliveryUnit: employee.deliveryUnit.toUpperCase(),
      employeeId: employee.employeeId,
    });
  }, [employee]);

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        onClick={(event) => {
          handleOpen();
          event.preventDefault();
          event.stopPropagation();
        }}
      >
        Update
      </Button>
      <GenericFormPopup
        formFields={dynamicFields}
        initialValues={initialValues}
        title="Employee Form"
        onSubmit={submitHandler}
        open={open}
        onClose={handleClose}
        autocompleteData={{
          reportsTo: reportsToData,
          secondLevelManager: secondLevelManagerData,
        }}
        loading={employeeMutation.isPending}
      />
      <StatusSnackbar
        status={status}
        successMessage="Employee updated successfully"
        errorMessage={errorMessage}
        handleClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      />
    </>
  );
};

export default UpdateEmployeePopup;

export const getId = (fieldValue: any) => {
  if (fieldValue && typeof fieldValue === "object" && "id" in fieldValue) {
    return fieldValue.id;
  } else if (fieldValue === "") {
    return null;
  }
  return fieldValue;
};
